// Notification DTOs shared by the server (inbox API, email, SSE) and the web client.
// The server never sends rendered copy: the client formats `kind` + `params` in the user's locale.
import { z } from "zod";
import type { Source } from "./principal.ts";

export const NOTIFICATION_KINDS = [
  "assigned",
  "mentioned",
  "commented",
  "status_changed",
  "closed",
  "reported_closed",
  "due_tomorrow",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/** Parameters per kind (the `params` of a NotificationView). Ids refer to rows the client already caches. */
export interface NotificationParams {
  /** `excerpt`: first line of the item's description (plain text, <= ~140 chars), "" when it has none. */
  assigned: { excerpt: string };
  /**
   * `in` says where the mention is; excerpt is the user's own text (first ~140 chars of the plain body).
   * `anchor` is the comment id to scroll to (null for a mention in the description); same value as `commentId`.
   */
  mentioned: { in: "description" | "comment"; commentId: string | null; anchor: string | null; excerpt: string };
  /**
   * `count` grows when several comments by the same person are coalesced; `excerpt` and `anchor` (= `commentId`,
   * the comment id to scroll to) are those of the latest one.
   */
  commented: { commentId: string; anchor: string; excerpt: string };
  status_changed: { fromStatusId: string | null; toStatusId: string; toCategory: string };
  closed: { fromStatusId: string | null; toStatusId: string; toCategory: string };
  reported_closed: { fromStatusId: string | null; toStatusId: string; toCategory: string };
  due_tomorrow: { dueDate: string };
}

export const EMAIL_DIGEST_MODES = ["off", "instant", "hourly", "daily"] as const;
export type EmailDigestMode = (typeof EMAIL_DIGEST_MODES)[number];

const KindToggles = z.object(
  Object.fromEntries(NOTIFICATION_KINDS.map((k) => [k, z.boolean().optional()])) as Record<
    NotificationKind,
    z.ZodOptional<z.ZodBoolean>
  >,
);

const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** Push stays quiet between `from` and `to` (wall clock in `tz`, may wrap midnight): nothing is sent then. */
export const QuietHours = z.object({ from: ClockTime, to: ClockTime, tz: z.string().min(1).max(64) });
export type QuietHours = z.infer<typeof QuietHours>;

/** PATCH body of /api/v1/me/notification-settings: every key optional, kinds merge into the stored toggles. */
export const NotificationSettingsPatch = z.object({
  inApp: KindToggles.optional(),
  email: KindToggles.optional(),
  push: KindToggles.optional(),
  pushFeedback: z.boolean().optional(),
  digest: z.enum(EMAIL_DIGEST_MODES).optional(),
  quietHours: QuietHours.nullable().optional(),
});
export type NotificationSettingsPatch = z.infer<typeof NotificationSettingsPatch>;

export interface NotificationSettings {
  /** Show this kind in the inbox. */
  inApp: Record<NotificationKind, boolean>;
  /** Email this kind (when `digest` is not "off"). */
  email: Record<NotificationKind, boolean>;
  /** Push this kind to the devices the user turned push on for. */
  push: Record<NotificationKind, boolean>;
  /** Push when a tester sends feedback for a product the user administers. */
  pushFeedback: boolean;
  /** off = never email, instant = right away, hourly | daily = one summary mail. */
  digest: EmailDigestMode;
  /** No push in this window (null = always). */
  quietHours: QuietHours | null;
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  inApp: {
    assigned: true,
    mentioned: true,
    commented: true,
    status_changed: true,
    closed: true,
    reported_closed: true,
    due_tomorrow: true,
  },
  email: {
    assigned: true,
    mentioned: true,
    commented: false,
    status_changed: false,
    closed: false,
    reported_closed: false,
    due_tomorrow: false,
  },
  push: {
    assigned: true,
    mentioned: true,
    commented: true,
    status_changed: false,
    closed: false,
    reported_closed: true,
    due_tomorrow: true,
  },
  pushFeedback: true,
  digest: "instant",
  quietHours: null,
};

/** Stored (partial) settings merged over the defaults. Unknown keys and wrong types are ignored. */
export function resolveNotificationSettings(stored: unknown): NotificationSettings {
  const d = DEFAULT_NOTIFICATION_SETTINGS;
  const s = (stored && typeof stored === "object" ? stored : {}) as Record<string, unknown>;
  const toggles = (key: "inApp" | "email" | "push") => {
    const raw = (s[key] && typeof s[key] === "object" ? s[key] : {}) as Record<string, unknown>;
    return Object.fromEntries(
      NOTIFICATION_KINDS.map((k) => [k, typeof raw[k] === "boolean" ? raw[k] : d[key][k]]),
    ) as Record<NotificationKind, boolean>;
  };
  const digest = (EMAIL_DIGEST_MODES as readonly unknown[]).includes(s.digest)
    ? (s.digest as EmailDigestMode)
    : d.digest;
  const quiet = QuietHours.safeParse(s.quietHours);
  return {
    inApp: toggles("inApp"),
    email: toggles("email"),
    push: toggles("push"),
    pushFeedback: typeof s.pushFeedback === "boolean" ? s.pushFeedback : d.pushFeedback,
    digest,
    quietHours: quiet.success ? quiet.data : null,
  };
}

export interface NotificationActor {
  id: string;
  name: string;
  image: string | null;
  kind: "human" | "bot";
}

/** The person a bot acted for ("Claude (via Cedric)"); only set when `actor` is a bot with a delegating owner. */
export interface NotificationVia {
  id: string;
  name: string;
}

export interface NotificationItemRef {
  id: string;
  /** Current key (item keys can change when an item moves). */
  key: string;
  title: string;
  productId: string;
  statusId: string;
}

/** One inbox row as returned by GET /api/v1/notifications. */
export interface NotificationView<K extends NotificationKind = NotificationKind> {
  id: string;
  kind: K;
  /** How many changes were merged into this row (a burst by the same person within two minutes). Min 1. */
  count: number;
  item: NotificationItemRef;
  /** Who did it; null for due_tomorrow and for deleted accounts. */
  actor: NotificationActor | null;
  /** The human the (bot) actor acted for, from the source event's via_user_id; null otherwise. */
  via: NotificationVia | null;
  params: NotificationParams[K];
  readAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  /** Bumped when more changes are merged in; the inbox sorts by this. */
  updatedAt: string;
}

export interface NotificationList {
  items: NotificationView[];
  nextCursor: string | null;
  unread: number;
}

/** Payload of the `notification.created` SSE event (delivered only to `userId`). */
export interface NotificationCreatedPayload {
  userId: string;
  notificationId: string;
  notificationKind: NotificationKind;
  itemId: string | null;
  /** true when an existing unread notification was updated in place (coalesced). */
  coalesced: boolean;
}

/**
 * Payload of the `item.meta` SSE event: something about the item that is not part of its ItemLite row changed,
 * so open detail views should refetch it. Sent to readers of the item's product.
 */
export interface ItemMetaPayload {
  itemId: string;
  what: "watchers" | "backlinks";
}

export type WatchState = "watching" | "muted" | "none";

export interface WatchStatus {
  state: WatchState;
  /** manual | author | assignee | mention | comment; null when state is none. */
  reason: string | null;
  watcherCount: number;
}

export const WatchPut = z.object({ state: z.enum(["watching", "muted"]).default("watching") });

export interface BacklinkView {
  /** Item that mentions the target. */
  item: NotificationItemRef;
  commentId: string | null;
  createdAt: string;
}

export interface MentionableUser {
  id: string;
  name: string;
  handle: string;
  image: string | null;
}

/** Event kinds a webhook may subscribe to (the "ping" test event is always allowed). */
export const WEBHOOK_EVENT_KINDS = [
  "item.created",
  "item.updated",
  "item.moved",
  "item.deleted",
  "item.restored",
  "comment.created",
  "relation.created",
  "relation.deleted",
  "milestone.created",
  "milestone.updated",
  "space.created",
  "product.created",
] as const;
export type WebhookEventKind = (typeof WEBHOOK_EVENT_KINDS)[number];

export interface WebhookView {
  id: string;
  name: string;
  url: string;
  /** Empty = every kind. */
  events: string[];
  /** null = instance-wide. */
  productId: string | null;
  createdBy: string | null;
  active: boolean;
  consecutiveFailures: number;
  disabledReason: string | null;
  disabledAt: string | null;
  lastDeliveryAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryView {
  id: string;
  webhookId: string;
  event: string;
  eventSeq: number | null;
  status: "pending" | "success" | "failed";
  attempts: number;
  nextAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  lastDurationMs: number | null;
  deliveredAt: string | null;
  createdAt: string;
}

/** GET /webhooks/:id/deliveries/:deliveryId: the list row plus what was sent and received. */
export interface WebhookDeliveryDetail extends WebhookDeliveryView {
  /** The JSON body that was POSTed. */
  payload: Record<string, unknown>;
  /** First ~1000 characters of the receiver's response body. */
  lastResponse: string | null;
  /**
   * Request headers of the latest attempt (Content-Type, User-Agent, X-Plannello-Event, X-Plannello-Delivery,
   * X-Plannello-Signature = HMAC of the body, derived from the secret; the secret is never stored). null when
   * no attempt has been made yet or the attempt predates this field.
   */
  requestHeaders: Record<string, string> | null;
}

/** The JSON body POSTed to a webhook. */
export interface WebhookEnvelope<P = unknown> {
  /** Delivery id (also in X-Plannello-Delivery); stable across retries, use it to deduplicate. */
  id: string;
  event: string;
  seq: number | null;
  at: string;
  productIds: string[];
  actor: { id: string | null; kind: "human" | "bot" | "system" };
  viaUserId: string | null;
  source: Source;
  data: P;
}

// ---------------------------------------------------------------------------------------------------------
// Web Push
// ---------------------------------------------------------------------------------------------------------

/** A browser's PushSubscription (`subscription.toJSON()`), as the client posts it. */
export const PushSubscriptionInput = z.object({
  endpoint: z.url().max(2000),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionInput>;

export interface PushDeviceView {
  id: string;
  label: string | null;
  createdAt: string;
  lastSuccessAt: string | null;
}

/** GET /api/v1/me/push: the server's public key (null = push not available) and the user's devices. */
export interface PushStatusView {
  publicKey: string | null;
  devices: PushDeviceView[];
}

/** What the service worker gets: already in the recipient's language. */
export interface PushMessage {
  title: string;
  body: string;
  /** Path in the app to open on tap. */
  url: string;
  /** Messages with the same tag replace each other on the device. */
  tag: string;
  renotify?: boolean;
  /** Epoch ms of the event. */
  ts?: number;
}
